import { Field, FormConsumer, FormProvider } from "@formily/react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../../styled-system/css";
import { HStack, Wrap } from "../../../../../styled-system/jsx";
import { Button } from "../../../../components/core/basic/Button";
import { CurrencyInput, NumberInput } from "../../../../components/fields/formily/NumberInput";
import { Select } from "../../../../components/fields/formily/Select";
import { useFormily } from "../../../../hooks/useFormily";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import { ALGORITHM_OPTIONS } from "../../../../components/core/blocks/AdPlan/constants";
import { useResolvedLabels } from "../../../../i18n/useResolvedLabels";

const ALGORITHM_OPTIONS_LIST = Object.values(ALGORITHM_OPTIONS);

export interface SimulationParameters {
  algorithm?: string;
  min_bid?: number;
  max_bid?: number;
  max_acos?: number;
  min_acos?: number;
  min_roas?: number;
  target_roas?: number;
  daily_budget?: number;
}

interface Props {
  initialValues?: Partial<SimulationParameters>;
  onApply: (parameters: SimulationParameters) => void;
  isPending: boolean;
}

export function SimulationParametersForm({ initialValues, onApply, isPending }: Props) {
  const { t } = useTranslation(["advertising"]);
  const acosOrRoas = useAcosOrRoas();
  const isRoas = acosOrRoas(false, true);
  const algorithmOptions = useResolvedLabels(ALGORITHM_OPTIONS_LIST, ["advertising"]);

  const form = useFormily<SimulationParameters>({
    initialValues: {
      algorithm: ALGORITHM_OPTIONS.PRODUCT_LAUNCH.value,
      ...initialValues,
    },
  });

  return (
    <FormProvider form={form}>
      <Wrap gap="medium" alignItems="end" className={css({ padding: "small" })}>
        <Field
          name="algorithm"
          title={t(($) => $.advertising["Algorithm"])}
          component={[Select, { css: css.raw({ flex: 1, minWidth: "200px" }) }]}
          dataSource={algorithmOptions}
        />
        <HStack gap="small" alignItems="end" className={css({ flex: 1, minWidth: "200px" })}>
          <Field
            name="min_bid"
            title={t(($) => $.advertising["Min Bid"])}
            component={[CurrencyInput, { css: css.raw({ flex: 1, minWidth: "100px" }) }]}
            initialValue={NaN}
          />
          <Field
            name="max_bid"
            title={t(($) => $.advertising["Max Bid"])}
            component={[CurrencyInput, { css: css.raw({ flex: 1, minWidth: "100px" }) }]}
            initialValue={NaN}
          />
        </HStack>
        {isRoas ?
          <HStack gap="small" alignItems="end" className={css({ flex: 1, minWidth: "200px" })}>
            <Field
              name="min_roas"
              title={t(($) => $.advertising["Low ROAS"])}
              component={[NumberInput, { css: css.raw({ flex: 1, minWidth: "100px" }) }]}
              initialValue={NaN}
            />
            <Field
              name="target_roas"
              title={t(($) => $.advertising["High ROAS"])}
              component={[NumberInput, { css: css.raw({ flex: 1, minWidth: "100px" }) }]}
              initialValue={NaN}
            />
          </HStack>
        : <HStack gap="small" alignItems="end" className={css({ flex: 1, minWidth: "200px" })}>
            <Field
              name="min_acos"
              title={t(($) => $.advertising["Low ACOS"])}
              component={[NumberInput, { endIcon: "%", css: css.raw({ flex: 1, minWidth: "100px" }) }]}
              initialValue={NaN}
            />
            <Field
              name="max_acos"
              title={t(($) => $.advertising["High ACOS"])}
              component={[NumberInput, { endIcon: "%", css: css.raw({ flex: 1, minWidth: "100px" }) }]}
              initialValue={NaN}
            />
          </HStack>
        }
        <Field
          name="daily_budget"
          title={t(($) => $.advertising["Daily Budget"])}
          component={[CurrencyInput, { css: css.raw({ flex: 1, minWidth: "120px" }) }]}
          initialValue={NaN}
        />
        <FormConsumer>
          {(form) => (
            <Button
              isPending={isPending}
              onPress={() => {
                void form.submit((values) => {
                  onApply(values);
                });
              }}
            >
              {t(($) => $.advertising["Simulate"])}
            </Button>
          )}
        </FormConsumer>
      </Wrap>
    </FormProvider>
  );
}
